import {
  AboveGradeWallType,
  BasementFloorType,
  BelowGradeWallType,
  CeilingBelowAtticType,
  CeilingBelowRoofType,
  ExposureDirectionForCompassType,
  ExposureDirectionType,
  FloorOverExposedSpaceType,
  FloorOverUnconditionedSpaceType,
  SizingCalculatorAttributesType,
  SizingItemsType,
  SlabFloorType
} from 'data/types';
import { nanoid } from 'nanoid';
import { getOrientedEdge } from 'utils/structure';
import { isValidSizingItem } from 'utils/sizing';

export type EdgeType = 'top' | 'right' | 'bottom' | 'left';

export type WallsByEdge = {
  top: (AboveGradeWallType | BelowGradeWallType)[];
  right: (AboveGradeWallType | BelowGradeWallType)[];
  bottom: (AboveGradeWallType | BelowGradeWallType)[];
  left: (AboveGradeWallType | BelowGradeWallType)[];
  unpositioned: (AboveGradeWallType | BelowGradeWallType)[];
};

export function isAboveGradeWall(
  wall: AboveGradeWallType | BelowGradeWallType
): wall is AboveGradeWallType {
  return 'wallType' in wall && !('belowGradeDepth' in wall);
}

export function aboveToBelowWall(
  wall: AboveGradeWallType,
  defaultSizingItems: SizingItemsType
): BelowGradeWallType {
  const next = {
    ...defaultSizingItems.belowGradeWall,
    uniqueId: wall.uniqueId,
    width: wall.width,
    height: wall.height,
    exposure: wall.exposure,
    verticalGlass: wall.verticalGlass || [],
    solidDoors: wall.solidDoors || [],
    modified: true
  } as BelowGradeWallType;

  return { ...next, valid: isValidSizingItem('belowGradeWalls', next) };
}

export function belowToAboveWall(
  wall: BelowGradeWallType,
  defaultSizingItems: SizingItemsType,
  homeOrientation: ExposureDirectionType | undefined
): AboveGradeWallType {
  const next = {
    ...defaultSizingItems.aboveGradeWall,
    uniqueId: wall.uniqueId,
    width: wall.width,
    height: wall.height,
    exposure: wall.exposure,
    edge: getOrientedEdge(wall.exposure, homeOrientation),
    verticalGlass: wall.verticalGlass || [],
    solidDoors: wall.solidDoors || [],
    modified: true
  } as AboveGradeWallType;

  return { ...next, valid: isValidSizingItem('aboveGradeWalls', next) };
}

export function groupWallsByEdge(
  aboveGradeWalls: AboveGradeWallType[],
  belowGradeWalls: BelowGradeWallType[],
  homeOrientation: ExposureDirectionType | undefined
): WallsByEdge {
  const result: WallsByEdge = {
    top: [],
    right: [],
    bottom: [],
    left: [],
    unpositioned: []
  };

  const allWalls = [
    ...aboveGradeWalls.map((w) => ({ ...w, _type: 'above' as const })),
    ...belowGradeWalls.map((w) => ({ ...w, _type: 'below' as const }))
  ];

  for (const wall of allWalls) {
    const edge = getOrientedEdge(wall.exposure, homeOrientation);
    if (edge) {
      result[edge].push(wall);
    } else {
      result.unpositioned.push(wall);
    }
  }

  return result;
}

export function createWallCrudFunctions(
  sizingAttributes: SizingCalculatorAttributesType | undefined,
  updateSizingAttributes: (
    updates: Partial<SizingCalculatorAttributesType>
  ) => void,
  defaultSizingItems: SizingItemsType,
  defaultWallWidth: number,
  defaultWallHeight: number,
  homeOrientation?: ExposureDirectionType
) {
  const aboveGradeWalls = sizingAttributes?.aboveGradeWalls || [];
  const belowGradeWalls = sizingAttributes?.belowGradeWalls || [];

  const addAboveGradeWall = (direction: ExposureDirectionForCompassType) => {
    updateSizingAttributes({
      aboveGradeWalls: [
        ...aboveGradeWalls,
        {
          ...defaultSizingItems.aboveGradeWall,
          uniqueId: nanoid(5),
          valid: true,
          exposure: direction,
          width: defaultWallWidth,
          height: defaultWallHeight,
          verticalGlass: [],
          solidDoors: []
        } as AboveGradeWallType
      ]
    });
  };

  const updateAboveGradeWall = (
    wallOrFenestrationId: string,
    updates: Partial<AboveGradeWallType>
  ) => {
    updateSizingAttributes({
      aboveGradeWalls: aboveGradeWalls.map((wall) =>
        wall.uniqueId === wallOrFenestrationId
          ? {
              ...wall,
              ...updates,
              modified: true
            }
          : {
              ...wall,
              verticalGlass: (wall.verticalGlass || []).map((glass) =>
                glass.uniqueId === wallOrFenestrationId
                  ? { ...glass, ...updates, modified: true }
                  : glass
              ),
              solidDoors: (wall.solidDoors || []).map((door) =>
                door.uniqueId === wallOrFenestrationId
                  ? { ...door, ...updates, modified: true }
                  : door
              )
            }
      )
    });
  };

  const duplicateAboveGradeWall = (uniqueId: string) => {
    const wall = aboveGradeWalls.find((w) => w.uniqueId === uniqueId);
    if (wall) {
      updateSizingAttributes({
        aboveGradeWalls: [...aboveGradeWalls, { ...wall, uniqueId: nanoid(5) }]
      });
    }
  };

  const addBelowGradeWall = (direction: ExposureDirectionForCompassType) => {
    updateSizingAttributes({
      belowGradeWalls: [
        ...belowGradeWalls,
        {
          ...defaultSizingItems.belowGradeWall,
          uniqueId: nanoid(5),
          valid: true,
          exposure: direction,
          width: defaultWallWidth,
          height: defaultWallHeight,
          verticalGlass: [],
          solidDoors: []
        } as BelowGradeWallType
      ]
    });
  };

  const updateBelowGradeWall = (
    wallOrFenestrationId: string,
    updates: Partial<BelowGradeWallType>
  ) => {
    updateSizingAttributes({
      belowGradeWalls: belowGradeWalls.map((wall) =>
        wall.uniqueId === wallOrFenestrationId
          ? {
              ...wall,
              ...updates,
              modified: true
            }
          : {
              ...wall,
              verticalGlass: (wall.verticalGlass || []).map((glass) =>
                glass.uniqueId === wallOrFenestrationId
                  ? { ...glass, ...updates, modified: true }
                  : glass
              ),
              solidDoors: (wall.solidDoors || []).map((door) =>
                door.uniqueId === wallOrFenestrationId
                  ? { ...door, ...updates, modified: true }
                  : door
              )
            }
      )
    });
  };

  const duplicateBelowGradeWall = (uniqueId: string) => {
    const wall = belowGradeWalls.find((w) => w.uniqueId === uniqueId);
    if (wall) {
      updateSizingAttributes({
        belowGradeWalls: [...belowGradeWalls, { ...wall, uniqueId: nanoid(5) }]
      });
    }
  };

  const deleteWall = (wallId: string) => {
    updateSizingAttributes({
      aboveGradeWalls: aboveGradeWalls.filter(
        (wall) => wall.uniqueId !== wallId
      ),
      belowGradeWalls: belowGradeWalls.filter(
        (wall) => wall.uniqueId !== wallId
      )
    });
  };

  const switchWallGrade = (wallId: string) => {
    const aboveWall = aboveGradeWalls.find((wall) => wall.uniqueId === wallId);
    if (aboveWall) {
      const collides = belowGradeWalls.some(
        (wall) => wall.exposure === aboveWall.exposure
      );
      if (collides) {
        return;
      }

      updateSizingAttributes({
        aboveGradeWalls: aboveGradeWalls.filter(
          (wall) => wall.uniqueId !== wallId
        ),
        belowGradeWalls: [
          ...belowGradeWalls,
          aboveToBelowWall(aboveWall, defaultSizingItems)
        ]
      });
      return;
    }

    const belowWall = belowGradeWalls.find((wall) => wall.uniqueId === wallId);
    if (belowWall) {
      const collides = aboveGradeWalls.some(
        (wall) => wall.exposure === belowWall.exposure
      );
      if (collides) {
        return;
      }

      updateSizingAttributes({
        belowGradeWalls: belowGradeWalls.filter(
          (wall) => wall.uniqueId !== wallId
        ),
        aboveGradeWalls: [
          ...aboveGradeWalls,
          belowToAboveWall(belowWall, defaultSizingItems, homeOrientation)
        ]
      });
    }
  };

  return {
    addAboveGradeWall,
    updateAboveGradeWall,
    duplicateAboveGradeWall,
    addBelowGradeWall,
    updateBelowGradeWall,
    duplicateBelowGradeWall,
    deleteWall,
    switchWallGrade
  };
}

export function createCeilingCrudFunctions(
  sizingAttributes: SizingCalculatorAttributesType | undefined,
  updateSizingAttributes: (
    updates: Partial<SizingCalculatorAttributesType>
  ) => void,
  defaultSizingItems: SizingItemsType,
  defaultArea: number
) {
  const ceilingsBelowRoof = sizingAttributes?.ceilingsBelowRoof || [];
  const ceilingsBelowAttic = sizingAttributes?.ceilingsBelowAttic || [];

  const addCeilingBelowRoof = () => {
    updateSizingAttributes({
      ceilingsBelowRoof: [
        ...ceilingsBelowRoof,
        {
          ...defaultSizingItems.ceilingBelowRoofJoists,
          uniqueId: nanoid(5),
          valid: true,
          area: defaultArea,
          skylights: []
        } as CeilingBelowRoofType
      ]
    });
  };

  const addCeilingOnExposedBeams = () => {
    updateSizingAttributes({
      ceilingsBelowRoof: [
        ...ceilingsBelowRoof,
        {
          ...defaultSizingItems.ceilingOnExposedBeams,
          uniqueId: nanoid(5),
          valid: true,
          area: defaultArea,
          skylights: []
        } as CeilingBelowRoofType
      ]
    });
  };

  const updateCeilingBelowRoof = (
    ceilingOrFenestrationId: string,
    updates: Partial<CeilingBelowRoofType>
  ) => {
    updateSizingAttributes({
      ceilingsBelowRoof: ceilingsBelowRoof.map((ceiling) =>
        ceiling.uniqueId === ceilingOrFenestrationId
          ? {
              ...ceiling,
              ...updates,
              modified: true
            }
          : {
              ...ceiling,
              skylights: (ceiling.skylights || []).map((skylight) =>
                skylight.uniqueId === ceilingOrFenestrationId
                  ? { ...skylight, ...updates, modified: true }
                  : skylight
              )
            }
      )
    });
  };

  const duplicateCeilingBelowRoof = (uniqueId: string) => {
    const ceiling = ceilingsBelowRoof.find((c) => c.uniqueId === uniqueId);
    if (ceiling) {
      updateSizingAttributes({
        ceilingsBelowRoof: [
          ...ceilingsBelowRoof,
          { ...ceiling, uniqueId: nanoid(5) }
        ]
      });
    }
  };

  const addCeilingBelowAttic = () => {
    updateSizingAttributes({
      ceilingsBelowAttic: [
        ...ceilingsBelowAttic,
        {
          ...defaultSizingItems.ceilingBelowAttic,
          uniqueId: nanoid(5),
          valid: true,
          area: defaultArea
        } as CeilingBelowAtticType
      ]
    });
  };

  const updateCeilingBelowAttic = (
    ceilingId: string,
    updates: Partial<CeilingBelowAtticType>
  ) => {
    updateSizingAttributes({
      ceilingsBelowAttic: ceilingsBelowAttic.map((ceiling) =>
        ceiling.uniqueId === ceilingId
          ? { ...ceiling, ...updates, modified: true }
          : ceiling
      )
    });
  };

  const duplicateCeilingBelowAttic = (uniqueId: string) => {
    const ceiling = ceilingsBelowAttic.find((c) => c.uniqueId === uniqueId);
    if (ceiling) {
      updateSizingAttributes({
        ceilingsBelowAttic: [
          ...ceilingsBelowAttic,
          { ...ceiling, uniqueId: nanoid(5) }
        ]
      });
    }
  };

  const deleteCeiling = (ceilingId: string) => {
    updateSizingAttributes({
      ceilingsBelowRoof: ceilingsBelowRoof.filter(
        (ceiling) => ceiling.uniqueId !== ceilingId
      ),
      ceilingsBelowAttic: ceilingsBelowAttic.filter(
        (ceiling) => ceiling.uniqueId !== ceilingId
      )
    });
  };

  return {
    addCeilingBelowRoof,
    addCeilingOnExposedBeams,
    updateCeilingBelowRoof,
    duplicateCeilingBelowRoof,
    addCeilingBelowAttic,
    updateCeilingBelowAttic,
    duplicateCeilingBelowAttic,
    deleteCeiling
  };
}

export function createFloorCrudFunctions(
  sizingAttributes: SizingCalculatorAttributesType | undefined,
  updateSizingAttributes: (
    updates: Partial<SizingCalculatorAttributesType>
  ) => void,
  defaultSizingItems: SizingItemsType,
  defaultArea: number,
  defaultFloorExposedEdge: number,
  defaultWallWidth: number
) {
  const floorsOverUnconditionedSpace =
    sizingAttributes?.floorsOverUnconditionedSpace || [];
  const floorsOverExposedSpace = sizingAttributes?.floorsOverExposedSpace || [];
  const slabFloors = sizingAttributes?.slabFloors || [];
  const basementFloors = sizingAttributes?.basementFloors || [];

  const addFloorOverUnconditionedSpace = () => {
    updateSizingAttributes({
      floorsOverUnconditionedSpace: [
        ...floorsOverUnconditionedSpace,
        {
          ...defaultSizingItems.floorOverUnconditionedSpace,
          uniqueId: nanoid(5),
          valid: true,
          area: defaultArea
        } as FloorOverUnconditionedSpaceType
      ]
    });
  };

  const updateFloorOverUnconditionedSpace = (
    floorId: string,
    updates: Partial<FloorOverUnconditionedSpaceType>
  ) => {
    updateSizingAttributes({
      floorsOverUnconditionedSpace: floorsOverUnconditionedSpace.map((floor) =>
        floor.uniqueId === floorId
          ? { ...floor, ...updates, modified: true }
          : floor
      )
    });
  };

  const duplicateFloorOverUnconditionedSpace = (uniqueId: string) => {
    const floor = floorsOverUnconditionedSpace.find(
      (f) => f.uniqueId === uniqueId
    );
    if (floor) {
      updateSizingAttributes({
        floorsOverUnconditionedSpace: [
          ...floorsOverUnconditionedSpace,
          { ...floor, uniqueId: nanoid(5) }
        ]
      });
    }
  };

  const addFloorOverExposedSpace = () => {
    updateSizingAttributes({
      floorsOverExposedSpace: [
        ...floorsOverExposedSpace,
        {
          ...defaultSizingItems.floorOverExposedSpace,
          uniqueId: nanoid(5),
          valid: true,
          area: defaultArea
        } as FloorOverExposedSpaceType
      ]
    });
  };

  const updateFloorOverExposedSpace = (
    floorId: string,
    updates: Partial<FloorOverExposedSpaceType>
  ) => {
    updateSizingAttributes({
      floorsOverExposedSpace: floorsOverExposedSpace.map((floor) =>
        floor.uniqueId === floorId
          ? { ...floor, ...updates, modified: true }
          : floor
      )
    });
  };

  const duplicateFloorOverExposedSpace = (uniqueId: string) => {
    const floor = floorsOverExposedSpace.find((f) => f.uniqueId === uniqueId);
    if (floor) {
      updateSizingAttributes({
        floorsOverExposedSpace: [
          ...floorsOverExposedSpace,
          { ...floor, uniqueId: nanoid(5) }
        ]
      });
    }
  };

  const addSlabFloor = () => {
    updateSizingAttributes({
      slabFloors: [
        ...slabFloors,
        {
          ...defaultSizingItems.slabFloor,
          uniqueId: nanoid(5),
          valid: true,
          exposedEdgeFeet: defaultFloorExposedEdge,
          feetBelowGrade: 1
        } as SlabFloorType
      ]
    });
  };

  const updateSlabFloor = (
    floorId: string,
    updates: Partial<SlabFloorType>
  ) => {
    updateSizingAttributes({
      slabFloors: slabFloors.map((floor) =>
        floor.uniqueId === floorId
          ? { ...floor, ...updates, modified: true }
          : floor
      )
    });
  };

  const duplicateSlabFloor = (uniqueId: string) => {
    const floor = slabFloors.find((f) => f.uniqueId === uniqueId);
    if (floor) {
      updateSizingAttributes({
        slabFloors: [...slabFloors, { ...floor, uniqueId: nanoid(5) }]
      });
    }
  };

  const addBasementFloor = () => {
    const shortestSideWidth =
      [20, 24, 28, 32].find((width) => width >= defaultWallWidth) || 32;
    updateSizingAttributes({
      basementFloors: [
        ...basementFloors,
        {
          ...defaultSizingItems.basementFloor,
          uniqueId: nanoid(5),
          valid: true,
          area: defaultArea,
          shortestSideWidthFeet: shortestSideWidth
        } as BasementFloorType
      ]
    });
  };

  const updateBasementFloor = (
    floorId: string,
    updates: Partial<BasementFloorType>
  ) => {
    updateSizingAttributes({
      basementFloors: basementFloors.map((floor) =>
        floor.uniqueId === floorId
          ? { ...floor, ...updates, modified: true }
          : floor
      )
    });
  };

  const duplicateBasementFloor = (uniqueId: string) => {
    const floor = basementFloors.find((f) => f.uniqueId === uniqueId);
    if (floor) {
      updateSizingAttributes({
        basementFloors: [...basementFloors, { ...floor, uniqueId: nanoid(5) }]
      });
    }
  };

  const deleteFloor = (floorId: string) => {
    updateSizingAttributes({
      floorsOverUnconditionedSpace: floorsOverUnconditionedSpace.filter(
        (floor) => floor.uniqueId !== floorId
      ),
      floorsOverExposedSpace: floorsOverExposedSpace.filter(
        (floor) => floor.uniqueId !== floorId
      ),
      slabFloors: slabFloors.filter((floor) => floor.uniqueId !== floorId),
      basementFloors: basementFloors.filter(
        (floor) => floor.uniqueId !== floorId
      )
    });
  };

  return {
    addFloorOverUnconditionedSpace,
    updateFloorOverUnconditionedSpace,
    duplicateFloorOverUnconditionedSpace,
    addFloorOverExposedSpace,
    updateFloorOverExposedSpace,
    duplicateFloorOverExposedSpace,
    addSlabFloor,
    updateSlabFloor,
    duplicateSlabFloor,
    addBasementFloor,
    updateBasementFloor,
    duplicateBasementFloor,
    deleteFloor
  };
}
